import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { BYTE_SIZE_5_MB } from 'shared/constants/limits'
import { ReviewsApi } from 'entities/Review/api/review'

export class ImageUploaderStore {
  private _imageFile: null | File = null
  private _imageUrl = ''
  private _imageError = ''
  private _imageLoading = false

  constructor(url?: string) {
    if (url) this._imageUrl = url
    makeAutoObservable(this)
  }

  get imageUrl() {
    return this._imageUrl
  }

  get imageLoading() {
    return this._imageLoading
  }

  get imageError() {
    return this._imageError
  }

  setImageFile = (file: File) => {
    if (file.size > BYTE_SIZE_5_MB) {
      this._imageError = 'Upload PNGs, JPEGs under 5 MB'
      return
    }

    this._imageFile = file
    this._imageUrl = ''
    this._imageError = ''
    void this.uploadImage()
  }

  uploadImage = async () => {
    if (!this._imageFile) return

    try {
      runInAction(() => {
        this._imageLoading = true
      })

      const formData = new FormData()
      formData.append('file', this._imageFile)

      const {
        data: { url },
      } = await ReviewsApi.uploadReviewImage(formData)

      runInAction(() => {
        this._imageUrl = url
        this._imageFile = null
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        runInAction(() => {
          this._imageError = e.errors.getError('file') || 'Failed to upload image'
        })
      }
    } finally {
      runInAction(() => {
        this._imageLoading = false
      })
    }
  }

  removeImage = async () => {
    const defaultError = 'Failed to remove image'

    try {
      runInAction(() => {
        this._imageLoading = true
      })

      const {
        data: { deleted },
      } = await ReviewsApi.deleteReviewImage({ url: this._imageUrl })

      runInAction(() => {
        if (deleted) {
          this._imageUrl = ''
          this._imageFile = null
        } else {
          this._imageError = defaultError
        }
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        runInAction(() => {
          this._imageError = error.errors.getError('file') || defaultError
        })
      }
    } finally {
      runInAction(() => {
        this._imageLoading = false
      })
    }
  }
}
